import React, { useState, useEffect } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { fetchBookingWidget, updateBookingWidget, getTenantSlug } from '../../services/api.js';
import { Card, Btn, Field, Input, Toast, Spinner } from '../../components/ui.jsx';

export default function OnlineBooking({ property }) {
  const [widget,  setWidget]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState('');
  const [copied,  setCopied]  = useState('');

  const pid = property?.id;
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  // Always strip dashes/special chars from slug so it matches the format used in the main hall (e.g. "lavendarhotel")
  const rawSlug = property?.slug || getTenantSlug() || (property?.name || 'hotel');
  const slug = rawSlug.toLowerCase().replace(/[^a-z0-9]/g, '');
  const bookingUrl = `${window.location.origin}/?book=1&tenant=${slug}`;
  const embedCode = `<iframe\n  src="${bookingUrl}"\n  width="100%"\n  height="600"\n  frameborder="0"\n  style="border-radius:14px;box-shadow:0 4px 24px rgba(0,0,0,.1)"\n></iframe>`;

  useEffect(() => {
    if (!pid) return;
    setLoading(true);
    fetchBookingWidget(pid).catch(() => null).then(wg => {
      setWidget(wg || {
        enabled: true,
        primaryColor: '#1A7F8E',
        logoUrl: '',
        welcomeMessage: 'Book directly for the best rates — guaranteed',
        minAdvance: 0,
        maxAdvance: 365,
        minNights: 1,
        maxNights: 30,
        requireCreditCard: false,
        instantConfirm: true,
        cancellationPolicy: 'Free cancellation up to 24 hours before arrival',
      });
    }).finally(() => setLoading(false));
  }, [pid]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBookingWidget(pid, widget);
      showToast('✅ Settings saved');
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const copyText = (text, label) => {
    navigator.clipboard?.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
    showToast(`${label} copied!`);
  };

  const W = widget;

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner /></div>;

  return (
    <div className="fade-up">
      <style>{`
        .ob-toggle { position:relative; width:46px; height:26px; }
        .ob-toggle input { opacity:0; width:0; height:0; }
        .ob-slider { position:absolute; inset:0; background:#ccc; border-radius:99px; cursor:pointer; transition:.3s; }
        .ob-slider:before { content:''; position:absolute; height:20px; width:20px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:.3s; box-shadow:0 1px 4px rgba(0,0,0,.2); }
        input:checked + .ob-slider { background:${T.teal}; }
        input:checked + .ob-slider:before { transform:translateX(20px); }
      `}</style>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyLight} 100%)`,
        borderRadius: 18, padding: '24px 28px', marginBottom: 24,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 16, boxShadow: SHADOW.lg,
      }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <span style={{ fontSize:22 }}>🌐</span>
            <h1 style={{ fontFamily:FONTS.display, fontSize:24, fontWeight:700, color:'#fff', margin:0 }}>
              Online Booking
            </h1>
          </div>
          <p style={{ fontSize:12, color:'rgba(255,255,255,.6)', margin:0 }}>
            Configure your direct booking page · Share the link with guests
          </p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:12, color: W?.enabled ? '#4ADE80' : 'rgba(255,255,255,.5)', fontWeight:600 }}>
            {W?.enabled ? '● Live' : '○ Offline'}
          </span>
          <Btn variant="primary" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : '💾 Save'}
          </Btn>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:20 }}>

        {/* ── LEFT: Settings ──────────────────────────────────────────── */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Enable / Disable */}
          <div style={{
            background: W?.enabled ? `linear-gradient(135deg,${T.teal}12,${T.success}08)` : T.surface,
            border: `1.5px solid ${W?.enabled ? T.teal+'50' : T.border}`,
            borderRadius: 14, padding: '16px 20px',
            display: 'flex', justifyContent:'space-between', alignItems:'center',
          }}>
            <div>
              <p style={{ fontSize:14, fontWeight:700, color: W?.enabled ? T.teal : T.slate, marginBottom:3 }}>
                {W?.enabled ? '✅ Booking page is LIVE' : '⚫ Booking page is disabled'}
              </p>
              <p style={{ fontSize:11, color:T.muted }}>
                {W?.enabled
                  ? 'Guests can search and book rooms through your public link'
                  : 'Enable to let guests book directly online'}
              </p>
            </div>
            <label className="ob-toggle">
              <input type="checkbox" checked={W?.enabled||false}
                onChange={e => setWidget(p => ({...p, enabled:e.target.checked}))} />
              <span className="ob-slider" />
            </label>
          </div>

          {/* Branding */}
          <div style={{ background:T.white, borderRadius:14, border:`1px solid ${T.border}`, padding:'20px 22px' }}>
            <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:16,
              display:'flex', alignItems:'center', gap:8 }}>
              <span>🎨</span> Branding &amp; Appearance
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Welcome Message">
                  <Input
                    value={W?.welcomeMessage||''}
                    onChange={v => setWidget(p => ({...p, welcomeMessage:v}))}
                    placeholder="Book directly for the best rates"
                  />
                </Field>
              </div>
              <Field label="Brand Colour">
                <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                  <input type="color" value={W?.primaryColor||'#1A7F8E'}
                    onChange={e => setWidget(p => ({...p, primaryColor:e.target.value}))}
                    style={{ width:44, height:40, border:`1px solid ${T.border}`, borderRadius:8, cursor:'pointer', padding:3 }} />
                  <Input value={W?.primaryColor||''} onChange={v => setWidget(p => ({...p, primaryColor:v}))} style={{ flex:1 }} />
                </div>
              </Field>
              <Field label="Logo URL (optional)">
                <Input value={W?.logoUrl||''} onChange={v => setWidget(p => ({...p, logoUrl:v}))} placeholder="https://..." />
              </Field>
            </div>
          </div>

          {/* Booking Rules */}
          <div style={{ background:T.white, borderRadius:14, border:`1px solid ${T.border}`, padding:'20px 22px' }}>
            <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:16,
              display:'flex', alignItems:'center', gap:8 }}>
              <span>📋</span> Booking Rules
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <Field label="Min advance booking (days)">
                <Input type="number" min="0"
                  value={W?.minAdvance??0}
                  onChange={v => setWidget(p => ({...p, minAdvance:parseInt(v)||0}))} />
              </Field>
              <Field label="Max advance booking (days)">
                <Input type="number" min="1"
                  value={W?.maxAdvance??365}
                  onChange={v => setWidget(p => ({...p, maxAdvance:parseInt(v)||365}))} />
              </Field>
              <Field label="Min nights">
                <Input type="number" min="1"
                  value={W?.minNights??1}
                  onChange={v => setWidget(p => ({...p, minNights:parseInt(v)||1}))} />
              </Field>
              <Field label="Max nights">
                <Input type="number" min="1"
                  value={W?.maxNights??30}
                  onChange={v => setWidget(p => ({...p, maxNights:parseInt(v)||30}))} />
              </Field>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Cancellation Policy">
                  <Input value={W?.cancellationPolicy||''}
                    onChange={v => setWidget(p => ({...p, cancellationPolicy:v}))}
                    placeholder="Free cancellation up to 24h before arrival" />
                </Field>
              </div>
            </div>

            <div style={{ display:'flex', gap:20, marginTop:12, flexWrap:'wrap' }}>
              <label style={{ display:'flex', alignItems:'center', gap:9, cursor:'pointer' }}>
                <input type="checkbox" checked={W?.instantConfirm||false}
                  onChange={e => setWidget(p => ({...p, instantConfirm:e.target.checked}))}
                  style={{ width:16, height:16, accentColor:T.teal }} />
                <span style={{ fontSize:13, color:T.slate }}>Instant Confirmation</span>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:9, cursor:'pointer' }}>
                <input type="checkbox" checked={W?.requireCreditCard||false}
                  onChange={e => setWidget(p => ({...p, requireCreditCard:e.target.checked}))}
                  style={{ width:16, height:16, accentColor:T.teal }} />
                <span style={{ fontSize:13, color:T.slate }}>Require Credit Card</span>
              </label>
            </div>
          </div>

        </div>

        {/* ── RIGHT: URL, Embed, Preview ──────────────────────────────── */}
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

          {/* Booking URL */}
          <div style={{
            background: T.white, borderRadius: 14, border: `1px solid ${T.border}`,
            padding: '18px 20px', boxShadow: SHADOW.sm,
          }}>
            <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:12,
              display:'flex', alignItems:'center', gap:7 }}>
              🔗 Your Booking Link
            </p>
            <div style={{
              background: T.surface, borderRadius: 10, padding: '10px 14px',
              border: `1px solid ${T.border}`, marginBottom: 10,
            }}>
              <p style={{
                fontSize:11, color:T.teal, fontFamily:'monospace',
                wordBreak:'break-all', lineHeight:1.5, margin:0,
              }}>{bookingUrl}</p>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn size="sm" variant="primary" style={{ flex:1 }}
                onClick={() => copyText(bookingUrl, 'Link')}>
                {copied === 'Link' ? '✅ Copied!' : '📋 Copy Link'}
              </Btn>
              <Btn size="sm" variant="ghost"
                onClick={() => window.open(bookingUrl, '_blank')}>
                Open ↗
              </Btn>
            </div>
            <p style={{ fontSize:10, color:T.muted, marginTop:10, lineHeight:1.5 }}>
              Share on your website, WhatsApp, social media, or email signature.
            </p>
          </div>

          {/* Embed code */}
          <div style={{
            background: T.white, borderRadius: 14, border: `1px solid ${T.border}`,
            padding: '18px 20px', boxShadow: SHADOW.sm,
          }}>
            <p style={{ fontSize:13, fontWeight:700, color:T.navy, marginBottom:12,
              display:'flex', alignItems:'center', gap:7 }}>
              {'<>'} Embed on Website
            </p>
            <div style={{
              background: '#0f172a', borderRadius: 10, padding: '12px 14px',
              marginBottom: 10, overflowX: 'auto',
            }}>
              <pre style={{ fontSize:10, color:'#7dd3fc', margin:0, whiteSpace:'pre-wrap', lineHeight:1.6 }}>
                {embedCode}
              </pre>
            </div>
            <Btn size="sm" variant="ghost" style={{ width:'100%' }}
              onClick={() => copyText(embedCode, 'Code')}>
              {copied === 'Code' ? '✅ Copied!' : '📋 Copy Embed Code'}
            </Btn>
          </div>

          {/* Mini live preview */}
          <div style={{
            background: W?.primaryColor || T.teal,
            borderRadius: 14, padding: '18px 20px', boxShadow: SHADOW.sm,
          }}>
            <p style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,.7)',
              letterSpacing:1, textTransform:'uppercase', marginBottom:10 }}>
              Widget Preview
            </p>
            {W?.logoUrl && (
              <img src={W.logoUrl} alt="logo"
                style={{ height:36, objectFit:'contain', marginBottom:10, borderRadius:6 }} />
            )}
            <p style={{ fontSize:16, fontWeight:700, color:'#fff', lineHeight:1.3, marginBottom:14 }}>
              {W?.welcomeMessage || 'Book your stay'}
            </p>
            <div style={{ background:'rgba(255,255,255,.18)', borderRadius:10, padding:'10px 12px', marginBottom:8 }}>
              <p style={{ fontSize:10, color:'rgba(255,255,255,.7)', marginBottom:4 }}>Check-in / Check-out</p>
              <div style={{ display:'flex', gap:8 }}>
                <div style={{ flex:1, background:'rgba(255,255,255,.25)', borderRadius:7, padding:'7px 10px', fontSize:11, color:'#fff' }}>
                  Today
                </div>
                <div style={{ flex:1, background:'rgba(255,255,255,.25)', borderRadius:7, padding:'7px 10px', fontSize:11, color:'#fff' }}>
                  Tomorrow
                </div>
              </div>
            </div>
            <div style={{
              background:'#fff', borderRadius:8, padding:'10px 14px', textAlign:'center',
              fontSize:12, fontWeight:700, color:W?.primaryColor || T.teal, cursor:'pointer',
            }}>
              Search Available Rooms
            </div>
            {W?.instantConfirm && (
              <p style={{ fontSize:9, color:'rgba(255,255,255,.65)', textAlign:'center', marginTop:8 }}>
                ⚡ Instant confirmation · No hidden fees
              </p>
            )}
          </div>

        </div>
      </div>

      <Toast message={toast} />
    </div>
  );
}
